import { createAsyncThunk } from '@reduxjs/toolkit'
import AxiosClient from '../AxiosClient'
import { connectedPlatformPayload } from 'src/interfaces/Platform.interface'
import {isValidSyncStartDate} from 'src/utils/syncStartDate'

type PlatformQuery = {
  nature?: string
  search?: string
}

export const fetchSourcePlatformAsync = createAsyncThunk(
  'platform/fetchSourcePlatform',
  async function (payload, toolkit) {
    console.log('fetchSourcePlatformAsync action called');
    return await AxiosClient('GET', '/api/get-source-platform?event_name=product', [], toolkit, [])
  }
)

export const fetchPlatformsAsync = createAsyncThunk(
  'platform/fetchPlatforms',
  async function (query: PlatformQuery | undefined, toolkit) {
    let queryString = ''
    const queryParams: any = []

    if (query?.nature) {
      queryParams.push(`nature=${query.nature}`)
    }

    if (query?.search) {
      queryParams.push(`search_string=${query.search}`)
    }

    if (queryParams.length > 0) {
      queryString = `?${queryParams.join('&')}`
    }
    return await AxiosClient('GET', `/available-platform${queryString}`, [], toolkit, [])
  }
)

export const fetchPlatformsWithQueryAsync = createAsyncThunk(
  'platform/fetchPlatforms',
  async function (payload: { type: string }, toolkit) {
    return await AxiosClient('GET', `/available-platform`, [], toolkit, payload)
  }
)

// Async action to connect a new platform
export const connectPlatformAsync = createAsyncThunk(
  'platform/connectPlatform',
  async function (payload: connectedPlatformPayload, toolkit) {
    return await AxiosClient('POST', '/platform', payload, toolkit, [])
  }
)

export const fetchConnectedPlatformAsync = createAsyncThunk(
  'platform/fetchConnectedPlatform',
  async function (payload, toolkit) {
    return await AxiosClient('GET', '/platform', [], toolkit, [])
  }
)

export const fetchHistoryLogPlatformsAsync = createAsyncThunk(
  'platform/fetchHistoryLogPlatforms',
  async function (payload, toolkit) {
    return await AxiosClient('GET', '/platform?include_inactive=true', [], toolkit, [])
  }
)

export const pollConnectedPlatformAsync = createAsyncThunk(
  'platform/pollConnectedPlatform',
  async function (payload, toolkit) {
    return await AxiosClient('GET', '/platform', [], toolkit, [])
  }
)

export const retrivePlatformDataAsync = createAsyncThunk(
  'platform/retrivePlatformData',
  async function (payload: { id: any; fetch_limited_data?: boolean }, toolkit) {
    const query = payload.fetch_limited_data ? '?fetch_limited_data=true' : ''
    const url = `/api/retrieve-platform-data-new/${payload.id}${query}`
    return await AxiosClient('GET', url, [], toolkit, [])
  }
)

export const deletePlatformDataAsync = createAsyncThunk(
  'platform/deletePlatformData',
  async function (payload: { id: number }, toolkit) {
    const url = `/platform/${payload.id}`
    return await AxiosClient('DELETE', url, [], toolkit, [])
  }
)

type CreatePlatformSettingsPayload = {
  platformId?: number | null
  [key: string]: any
}

const DATE_SETTING_KEYS = new Set(['sync_start_date', 'start_date'])

const normalizePlatformSettingDates = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizePlatformSettingDates(item))
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  const normalizedObject: Record<string, any> = {}

  Object.keys(value).forEach((key) => {
    const fieldValue = value[key]

    if (DATE_SETTING_KEYS.has(key)) {
      if (typeof fieldValue === 'string' && isValidSyncStartDate(fieldValue)) {
        normalizedObject[key] = fieldValue.trim()
      } else if (fieldValue instanceof Date) {
        const iso = fieldValue.toISOString()
        if (isValidSyncStartDate(iso)) {
          normalizedObject[key] = iso
        }
      }
      return
    }

    normalizedObject[key] = normalizePlatformSettingDates(fieldValue)
  })

  return normalizedObject
}

export const createPlatformSettingAsync = createAsyncThunk(
  'platform/createPlatformSetting',
  async function (payload: CreatePlatformSettingsPayload, toolkit) {
    const { platformId, ...requestBody } = payload

    if (platformId === undefined || platformId === null) {
      throw new Error('platformId is required to save platform settings')
    }

    const url = `/api/create-platform-settings/${platformId}/settings`
    const normalizedRequestBody = normalizePlatformSettingDates(requestBody)
    return await AxiosClient('POST', url, normalizedRequestBody, toolkit, [])
  }
)

export const refreshPlatformDataAsync = createAsyncThunk(
  'platform/refreshPlatformDataAsync',
  async function (params: { platformId: number }, toolkit) {
    return await AxiosClient('POST', `/refresh_init_data/${params.platformId}`, [], toolkit, [])
  }
)

// uninstall shopify

export const shopifyappUninstallAsync = createAsyncThunk(
  'platform/shopifyappUninstallAsync',
  async function (payload: { id: number }, toolkit) {
    const url = `/api/shopifyapp/webhook/platform_id/${payload.id}`
    return await AxiosClient('DELETE', url, [], toolkit, [])
  }
)

export const delistVariantsAsync = createAsyncThunk(
  'platform/delistVariantsAsync',
  async function (payload: { platform_id: number; delist_variant_ids: number[]; type: string }, toolkit) {
    return await AxiosClient('POST', '/api/delist-product-variant', payload, toolkit, [])
  }
)

export const getAuthRequirementSelectOptionsAsync = createAsyncThunk(
  'platform/getAuthRequirementSelectOptionsAsync',
  async function (payload: { auth_field_id: number }, toolkit) {
    return await AxiosClient('GET', `/platform_auth_requirement_option/${payload.auth_field_id}`, [], toolkit, [])
  }
)

export const getAuthRequirementCurrencyOptionsAsync = createAsyncThunk(
  'platform/getAuthRequirementCurrencyOptionsAsync',
  async function (payload, toolkit) {
    return await AxiosClient('GET', `/platform_currency_option/`, [], toolkit, [])
  }
)

export const getPlatformTokenDatasync = createAsyncThunk(
  'platform/getPlatformTokenDatasync',
  async function (params: any, toolkit) {
    return await AxiosClient('GET', `/api/temu-token/`, [], toolkit, params)
  }
)

export const setPlatformTokenDatasync = createAsyncThunk(
  'platform/setPlatformTokenDatasync',
  async function (payload: { id: any; payload: any }, toolkit) {
    return await AxiosClient('POST', `/api/temu-token/`, payload.payload, toolkit, payload.id)
  }
)

export const getPlatformConsentUrlAsync = createAsyncThunk(
  'platform/getPlatformConsentUrlAsync',
  async function (params: { platform_name: string | null }, toolkit) {
    return await AxiosClient('GET', `/consent-url?available_platform_name=${params.platform_name}`, [], toolkit, [])
  }
)

export const getLogicBrokerPartnersAsync = createAsyncThunk(
  'platform/getLogicBrokerPartnersAsync',
  async function (params: {account_type: string; key: string}, toolkit) {
    const accountType = encodeURIComponent(params.account_type)
    const key = encodeURIComponent(params.key)
    return await AxiosClient(
      'GET',
      `/api/logicbroker-partens?account_type=${accountType}&key=${key}`,
      [],
      toolkit,
      []
    )
  }
)

export const getPlatformOwnerFieldsAsync = createAsyncThunk(
  'platform/getPlatformOwnerFieldsAsync',
  async function (params: { platform_id: string | null; field_id: string }, toolkit) {
    return await AxiosClient('GET', `/api/platform-owner-fields/${params.platform_id}?field_id=${params.field_id}`, [], toolkit, [])
  }
)

export const updatePlatformAdditionalInfoStatusAsync = createAsyncThunk(
  'platform/updatePlatformAdditionalInfoStatusAsync',
  async function (params: { platform_id: string | null }, toolkit) {
    return await AxiosClient('POST', `/api/acknowledge-platform-additional-info-fetched/${params.platform_id}`, [], toolkit, [])
  }
)

export const getPlatformAdditionalInfoStatusAsync = createAsyncThunk(
  'platform/getPlatformAdditionalInfoStatusAsync',
  async function (params: { platform_id: string | null }, toolkit) {
    return await AxiosClient('GET', `/api/acknowledge-platform-additional-info-fetched/${params.platform_id}`, [], toolkit, [])
  }
)

type PlatformParams = { platform_id?: string }

export const getAllPlatformTypesAsync = createAsyncThunk(
  'platform/fetchedConnectedPlatformSlice',
  async (params: PlatformParams | undefined, toolkit) => {
    const query = params?.platform_id ? `?platform_id=${params?.platform_id}` : ''
    return await AxiosClient('GET', `/api/platform-typewise-list${query}`, [], toolkit, [])
  }
)
